/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { action } from 'mobx';

import { BaseStoreSelectorStateManager } from './base-store-selector-state-manager';
import { ILoadMapApiInput, IStoreSelectorLocation } from './i-store-selection-state-manager';
import { IMapWindow, MapLocationsResult } from './models/map-data';

declare const window: IMapWindow;

/**
 * Instance of IStoreSelectorStateManager manager that uses Map service
 * for geo location.
 */
export class MapStoreSelectorStateManager extends BaseStoreSelectorStateManager {
    private readonly SERVICE_ENDPOINT: string = 'https://dev.virtualearth.net/REST/v1/';

    // private readonly MAP_API_ENDPOINT: string = 'https://www.bing.com/api/maps/mapcontrol?callback=mapAPIReady';

    @action
    public async geoLocate(searchTerm: string, actionContext: IActionContext): Promise<IStoreSelectorLocation | undefined> {
        if (!searchTerm || !actionContext.requestContext.channel) {
            return undefined;
        }

        let searchContent: string = searchTerm;

        // const jsonp = `mapSearchCallback_${crypto.getRandomValues(new Uint32Array(1))[0]}`;

        if (actionContext.requestContext.channel.ChannelCountryRegionISOCode) {
            searchContent = `${searchTerm.trim()} ${actionContext.requestContext.channel.ChannelCountryRegionISOCode}`;
        }

        const query = encodeURI(searchContent);
        const key = '89mOAQw5I72jNOmsvvQ19hwMiLBIbdiVMb73GTK5xxVNtcMUZB9CJQQJ99AGACi5Ypz75dUeAAAgAZMPWHfw';

        // const geolocationApiUrl =
        //     actionContext.requestContext.app &&
        //     actionContext.requestContext.app.config &&
        //     actionContext.requestContext.app.config.geolocationApiUrl;
        // const requestUrl = `${
        //     geolocationApiUrl ? geolocationApiUrl : this.SERVICE_ENDPOINT
        // }/Locations?query=${query}&key=${key}&jsonp=${jsonp}`;

        const requestUrl = `https://atlas.microsoft.com/search/address/json?api-version=1.0&subscription-key=${key}&query=${query}`;

        try {
            const response = await fetch(requestUrl);
            if (!response.ok) {
                console.error('Azure Maps API call failed:', response.statusText);
                return undefined;
            }

            const data = await response.json();

            if (data && data.results && data.results.length > 0) {
                const position = data.results[0].position;
                return {
                    latitude: position.lat,
                    longitude: position.lon
                };
            }

            return undefined;
        } catch (error) {
            console.error('Error while calling Azure Maps API:', error);
            return undefined;
        }
    }

    @action
    public async loadMapApi(input: ILoadMapApiInput): Promise<void> {
        debugger;
        console.log('laod api key', input);
        input.key = '89mOAQw5I72jNOmsvvQ19hwMiLBIbdiVMb73GTK5xxVNtcMUZB9CJQQJ99AGACi5Ypz75dUeAAAgAZMPWHfw';
        if (StringExtensions.isNullOrEmpty(input.key)) {
            return Promise.resolve();
        }

        const isScriptAlreadyLoaded = !!document?.getElementById('azure-maps-api-script');

        if (isScriptAlreadyLoaded) {
            // Prevents from loading multiple script into the body
            return Promise.resolve();
        }

        const url = `https://atlas.microsoft.com/sdk/mapcontrol?api-version=2&subscription-key=${input.key}&language=${input.lang ||
            'en-US'}&market=${input.market || 'US'}`;

        return new Promise((resolve, reject) => {
            const script = document?.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.defer = true;
            script.src = url;
            script.id = 'azure-maps-api-script';
            if (window) {
                window.mapAPIReady = () => {
                    this.isMapApiLoaded = true;
                    resolve();
                };
            }
            script.addEventListener('error', (error: string | Event) => {
                reject(error);
            });
            document?.body?.appendChild(script);
        });
    }
}
